<!-- src/components/Users/UserHierarchy.vue -->
<template>
    <div class="hierarchy-container">
        <!-- Header -->
        <div class="hierarchy-header">
            <div class="header-left">
                <h2 class="hierarchy-title">Organizational Chart</h2>
                <p class="hierarchy-subtitle">View your team structure and reporting relationships</p>
            </div>
            <div class="header-right">
                <b-button-group size="sm">
                    <b-button 
                        variant="outline-secondary" 
                        @click="refreshHierarchy"
                        title="Refresh"
                    >
                        <b-icon icon="arrow-clockwise"></b-icon>
                    </b-button>
                    <b-button 
                        :variant="viewMode === 'org' ? 'primary' : 'outline-secondary'"
                        @click="viewMode = 'org'"
                        title="Org Chart View"
                    >
                        <b-icon icon="diagram-3"></b-icon>
                    </b-button>
                    <b-button 
                        :variant="viewMode === 'card' ? 'primary' : 'outline-secondary'"
                        @click="viewMode = 'card'"
                        title="Card View"
                    >
                        <b-icon icon="grid"></b-icon>
                    </b-button>
                </b-button-group>
            </div>
        </div>

        <!-- Search & Filters -->
        <div class="hierarchy-controls">
            <div class="search-wrapper">
                <b-icon icon="search" class="search-icon"></b-icon>
                <b-form-input
                    v-model="searchQuery"
                    placeholder="Search by name or username..."
                    @input="filterHierarchy"
                    class="search-input"
                ></b-form-input>
                <b-icon 
                    v-if="searchQuery" 
                    icon="x" 
                    class="clear-icon"
                    @click="searchQuery = ''; filterHierarchy()"
                ></b-icon>
            </div>
            <div class="filter-wrapper">
                <b-form-select
                    v-model="roleFilter"
                    :options="roleFilterOptions"
                    size="sm"
                    class="filter-select"
                    @change="filterHierarchy"
                >
                    <option :value="null">All Roles</option>
                </b-form-select>
                <b-form-select
                    v-model="statusFilter"
                    :options="statusFilterOptions"
                    size="sm"
                    class="filter-select"
                    @change="filterHierarchy"
                >
                    <option :value="null">All Status</option>
                </b-form-select>
            </div>
            <div class="status-toggle">
                <b-form-checkbox 
                    v-model="showOnlyActive" 
                    @change="filterHierarchy"
                    switch
                    size="sm"
                >
                    Show Active Only
                </b-form-checkbox>
            </div>
        </div>

        <!-- Loading -->
        <div v-if="items_being_loaded" class="loading-container">
            <div class="loader"></div>
            <p>Loading team structure...</p>
        </div>

        <!-- Content -->
        <div v-else class="hierarchy-content">
            <!-- Org Chart View -->
            <div v-if="viewMode === 'org'" class="org-chart-wrapper">
                <div v-if="getAllUsers().length > 0" class="org-tree">
                    
                    <!-- ============================================ -->
                    <!-- LEVEL 1: ADMIN -->
                    <!-- ============================================ -->
                    <div 
                        v-if="getUsersByRole('admin').length > 0"
                        class="tree-level"
                    >
                        <div class="level-label-badge">
                            <span class="level-icon">👑</span>
                            <span class="level-name">Executive Leadership</span>
                            <span class="level-count">{{ getUsersByRole('admin').length }}</span>
                        </div>
                        <div class="admin-row">
                            <div 
                                v-for="user in getUsersByRole('admin')" 
                                :key="user.id" 
                                class="admin-item"
                            >
                                <div 
                                    class="org-node"
                                    :class="{ 
                                        'highlighted': highlightUserId === user.id,
                                        'inactive': !user.active,
                                        'active-user': user.active
                                    }"
                                    @click="selectUser(user)"
                                >
                                    <div class="node-card level-1">
                                        <div class="node-avatar">
                                            <img 
                                                v-if="getUserImage(user)" 
                                                :src="getUserImage(user)" 
                                                :alt="getDisplayName(user)"
                                                class="avatar-image"
                                                @error="handleImageError"
                                            />
                                            <div v-else class="avatar-placeholder admin-avatar">
                                                {{ getInitials(getDisplayName(user)) }}
                                            </div>
                                            <div class="status-ring" :class="{ 'active': user.active }"></div>
                                        </div>
                                        <div class="node-info">
                                            <div class="node-name">{{ getDisplayName(user) }}</div>
                                            <div class="node-role">{{ formatRole(user.userrole) }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- ============================================ -->
                    <!-- CONNECTOR: Admin → Warehouse & Locations -->
                    <!-- ============================================ -->
                    <div 
                        class="tree-connector split-connector"
                        v-if="(getWarehouseGroups().length > 0) && (getLocationGroups().length > 0)"
                    >
                        <div class="connector-line vertical"></div>
                        <div class="connector-line horizontal split-line">
                            <div class="split-left-label">🏭 Warehouse</div>
                            <div class="split-right-label">🏪 Locations</div>
                        </div>
                        <div class="connector-line vertical left"></div>
                        <div class="connector-line vertical right"></div>
                    </div>

                    <!-- ============================================ -->
                    <!-- TWO BRANCHES SIDE BY SIDE -->
                    <!-- ============================================ -->
                    <div class="branches-row">
                        
                        <!-- ========================================== -->
                        <!-- LEFT BRANCH: WAREHOUSE -->
                        <!-- ========================================== -->
                        <div class="branch-container branch-warehouse">
                            
                            <!-- WAREHOUSE GROUPS - Each location with Manager + Users -->
                            <div 
                                v-if="getWarehouseGroups().length > 0"
                                class="tree-level"
                            >
                                <div class="level-label-badge branch-label">
                                    <span class="level-icon">🏭</span>
                                    <span class="level-name">Warehouse Management</span>
                                    <span class="level-count">{{ getWarehouseGroups().length }}</span>
                                </div>
                                
                                <!-- Each Warehouse Location Group -->
                                <div class="warehouse-groups-container">
                                    <div 
                                        v-for="group in getWarehouseGroups()" 
                                        :key="group.location"
                                        class="warehouse-group-card"
                                    >
                                        <!-- Location Header -->
                                        <div class="warehouse-group-header">
                                            <span class="location-icon">📍</span>
                                            <span class="location-name">{{ group.location }}</span>
                                            <span class="location-count">{{ group.totalUsers }}</span>
                                        </div>
                                        
                                        <!-- Warehouse Manager -->
                                        <div class="warehouse-manager-section" v-if="group.manager">
                                            <div class="node-wrapper">
                                                <div 
                                                    class="org-node"
                                                    :class="{ 
                                                        'highlighted': highlightUserId === group.manager.id,
                                                        'inactive': !group.manager.active,
                                                        'active-user': group.manager.active
                                                    }"
                                                    @click="selectUser(group.manager)"
                                                >
                                                    <div class="node-card level-2">
                                                        <div class="node-avatar">
                                                            <img 
                                                                v-if="getUserImage(group.manager)" 
                                                                :src="getUserImage(group.manager)" 
                                                                :alt="getDisplayName(group.manager)"
                                                                class="avatar-image"
                                                                @error="handleImageError"
                                                            />
                                                            <div v-else class="avatar-placeholder manager-avatar">
                                                                {{ getInitials(getDisplayName(group.manager)) }}
                                                            </div>
                                                            <div class="status-ring" :class="{ 'active': group.manager.active }"></div>
                                                        </div>
                                                        <div class="node-info">
                                                            <div class="node-name">{{ getDisplayName(group.manager) }}</div>
                                                            <div class="node-role">Manager</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Connector: Manager → Users -->
                                        <div class="location-connector" v-if="group.manager && group.users.length > 0">
                                            <div class="connector-line vertical small"></div>
                                        </div>

                                        <!-- Warehouse Users under this location -->
                                        <div class="warehouse-users-section" v-if="group.users.length > 0">
                                            <div class="staff-label">Staff</div>
                                            <div class="level-nodes">
                                                <div 
                                                    v-for="user in group.users" 
                                                    :key="user.id" 
                                                    class="node-wrapper"
                                                >
                                                    <div 
                                                        class="org-node"
                                                        :class="{ 
                                                            'highlighted': highlightUserId === user.id,
                                                            'inactive': !user.active,
                                                            'active-user': user.active
                                                        }"
                                                        @click="selectUser(user)"
                                                    >
                                                        <div class="node-card level-3">
                                                            <div class="node-avatar">
                                                                <img 
                                                                    v-if="getUserImage(user)" 
                                                                    :src="getUserImage(user)" 
                                                                    :alt="getDisplayName(user)"
                                                                    class="avatar-image"
                                                                    @error="handleImageError"
                                                                />
                                                                <div v-else class="avatar-placeholder staff-avatar">
                                                                    {{ getInitials(getDisplayName(user)) }}
                                                                </div>
                                                                <div class="status-ring" :class="{ 'active': user.active }"></div>
                                                            </div>
                                                            <div class="node-info">
                                                                <div class="node-name">{{ getDisplayName(user) }}</div>
                                                                <div class="node-role">{{ formatRole(user.userrole) }}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- CONNECTOR -->
                            <div 
                                class="tree-connector"
                                v-if="getWarehouseGroups().length > 0 && getUsersByRole('housetruck').length > 0"
                            >
                                <div class="connector-line vertical"></div>
                            </div>

                            <!-- HOUSE TRUCKS -->
                            <div 
                                v-if="getUsersByRole('housetruck').length > 0"
                                class="tree-level"
                            >
                                <div class="level-label-badge">
                                    <span class="level-icon">🚚</span>
                                    <span class="level-name">House Trucks</span>
                                    <span class="level-count">{{ getUsersByRole('housetruck').length }}</span>
                                </div>
                                <div class="level-nodes">
                                    <div 
                                        v-for="user in getUsersByRole('housetruck')" 
                                        :key="user.id" 
                                        class="node-wrapper"
                                    >
                                        <div 
                                            class="org-node"
                                            :class="{ 
                                                'highlighted': highlightUserId === user.id,
                                                'inactive': !user.active,
                                                'active-user': user.active
                                            }"
                                            @click="selectUser(user)"
                                        >
                                            <div class="node-card level-4">
                                                <div class="node-avatar">
                                                    <img 
                                                        v-if="getUserImage(user)" 
                                                        :src="getUserImage(user)" 
                                                        :alt="getDisplayName(user)"
                                                        class="avatar-image"
                                                        @error="handleImageError"
                                                    />
                                                    <div v-else class="avatar-placeholder truck-avatar">
                                                        {{ getInitials(getDisplayName(user)) }}
                                                    </div>
                                                    <div class="status-ring" :class="{ 'active': user.active }"></div>
                                                </div>
                                                <div class="node-info">
                                                    <div class="node-name">{{ getDisplayName(user) }}</div>
                                                    <div class="node-role">{{ formatRole(user.userrole) }}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- ========================================== -->
                        <!-- RIGHT BRANCH: LOCATIONS -->
                        <!-- ========================================== -->
                        <div class="branch-container branch-locations">
                            <!-- LOCATION GROUPS -->
                            <div 
                                v-if="getLocationGroups().length > 0"
                                class="tree-level"
                            >
                                <div class="level-label-badge branch-label">
                                    <span class="level-icon">🏪</span>
                                    <span class="level-name">Location Management</span>
                                    <span class="level-count">{{ getLocationGroups().length }}</span>
                                </div>
                                
                                <!-- Each Location Group -->
                                <div class="location-groups-container">
                                    <div 
                                        v-for="locationGroup in getLocationGroups()" 
                                        :key="locationGroup.location"
                                        class="location-group-card"
                                    >
                                        <!-- Location Header -->
                                        <div class="location-group-header">
                                            <span class="location-icon">📍</span>
                                            <span class="location-name">{{ locationGroup.location }}</span>
                                            <span class="location-count">{{ locationGroup.totalUsers }}</span>
                                        </div>
                                        
                                        <!-- Location Admin -->
                                        <div class="location-admin-section" v-if="locationGroup.admin">
                                            <div class="node-wrapper">
                                                <div 
                                                    class="org-node"
                                                    :class="{ 
                                                        'highlighted': highlightUserId === locationGroup.admin.id,
                                                        'inactive': !locationGroup.admin.active,
                                                        'active-user': locationGroup.admin.active
                                                    }"
                                                    @click="selectUser(locationGroup.admin)"
                                                >
                                                    <div class="node-card level-2">
                                                        <div class="node-avatar">
                                                            <img 
                                                                v-if="getUserImage(locationGroup.admin)" 
                                                                :src="getUserImage(locationGroup.admin)" 
                                                                :alt="getDisplayName(locationGroup.admin)"
                                                                class="avatar-image"
                                                                @error="handleImageError"
                                                            />
                                                            <div v-else class="avatar-placeholder manager-avatar">
                                                                {{ getInitials(getDisplayName(locationGroup.admin)) }}
                                                            </div>
                                                            <div class="status-ring" :class="{ 'active': locationGroup.admin.active }"></div>
                                                        </div>
                                                        <div class="node-info">
                                                            <div class="node-name">{{ getDisplayName(locationGroup.admin) }}</div>
                                                            <div class="node-role">Admin</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Connector: Admin → Salespersons -->
                                        <div class="location-connector" v-if="locationGroup.admin && locationGroup.salespersons.length > 0">
                                            <div class="connector-line vertical small"></div>
                                        </div>

                                        <!-- Salespersons under this location -->
                                        <div class="location-sales-section" v-if="locationGroup.salespersons.length > 0">
                                            <div class="sales-label">Sales</div>
                                            <div class="level-nodes">
                                                <div 
                                                    v-for="user in locationGroup.salespersons" 
                                                    :key="user.id" 
                                                    class="node-wrapper"
                                                >
                                                    <div 
                                                        class="org-node"
                                                        :class="{ 
                                                            'highlighted': highlightUserId === user.id,
                                                            'inactive': !user.active,
                                                            'active-user': user.active
                                                        }"
                                                        @click="selectUser(user)"
                                                    >
                                                        <div class="node-card level-3">
                                                            <div class="node-avatar">
                                                                <img 
                                                                    v-if="getUserImage(user)" 
                                                                    :src="getUserImage(user)" 
                                                                    :alt="getDisplayName(user)"
                                                                    class="avatar-image"
                                                                    @error="handleImageError"
                                                                />
                                                                <div v-else class="avatar-placeholder staff-avatar">
                                                                    {{ getInitials(getDisplayName(user)) }}
                                                                </div>
                                                                <div class="status-ring" :class="{ 'active': user.active }"></div>
                                                            </div>
                                                            <div class="node-info">
                                                                <div class="node-name">{{ getDisplayName(user) }}</div>
                                                                <div class="node-role">{{ formatRole(user.userrole) }}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- CONNECTOR -->
                            <div 
                                class="tree-connector"
                                v-if="getLocationGroups().length > 0 && getUsersByRole('deliverytruck').length > 0"
                            >
                                <div class="connector-line vertical"></div>
                            </div>

                            <!-- DELIVERY TRUCKS -->
                            <div 
                                v-if="getUsersByRole('deliverytruck').length > 0"
                                class="tree-level"
                            >
                                <div class="level-label-badge">
                                    <span class="level-icon">🚚</span>
                                    <span class="level-name">Delivery Trucks</span>
                                    <span class="level-count">{{ getUsersByRole('deliverytruck').length }}</span>
                                </div>
                                <div class="level-nodes">
                                    <div 
                                        v-for="user in getUsersByRole('deliverytruck')" 
                                        :key="user.id" 
                                        class="node-wrapper"
                                    >
                                        <div 
                                            class="org-node"
                                            :class="{ 
                                                'highlighted': highlightUserId === user.id,
                                                'inactive': !user.active,
                                                'active-user': user.active
                                            }"
                                            @click="selectUser(user)"
                                        >
                                            <div class="node-card level-4">
                                                <div class="node-avatar">
                                                    <img 
                                                        v-if="getUserImage(user)" 
                                                        :src="getUserImage(user)" 
                                                        :alt="getDisplayName(user)"
                                                        class="avatar-image"
                                                        @error="handleImageError"
                                                    />
                                                    <div v-else class="avatar-placeholder truck-avatar">
                                                        {{ getInitials(getDisplayName(user)) }}
                                                    </div>
                                                    <div class="status-ring" :class="{ 'active': user.active }"></div>
                                                </div>
                                                <div class="node-info">
                                                    <div class="node-name">{{ getDisplayName(user) }}</div>
                                                    <div class="node-role">{{ formatRole(user.userrole) }}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Empty State -->
                <div v-else class="empty-state">
                    <div class="empty-icon">
                        <b-icon icon="people" font-scale="3"></b-icon>
                    </div>
                    <h4>No team members found</h4>
                    <p v-if="hasActiveFilters">Try adjusting your search or filters</p>
                    <p v-else>Start building your team by adding users</p>
                    <b-button 
                        v-if="isAdmin && !hasActiveFilters" 
                        variant="primary" 
                        @click="$emit('add-user')"
                    >
                        <b-icon icon="person-plus" class="mr-2"></b-icon>
                        Add First Member
                    </b-button>
                </div>
            </div>

            <!-- Card View -->
            <div v-else class="card-view">
                <div class="user-grid">
                    <div 
                        v-for="user in filteredUsers" 
                        :key="user.id"
                        class="user-card"
                        :class="{ 
                            'selected': selectedUser && selectedUser.id === user.id,
                            'inactive': !user.active,
                            'active-user': user.active
                        }"
                        @click="selectUser(user)"
                    >
                        <div class="card-avatar">
                            <img 
                                v-if="getUserImage(user)" 
                                :src="getUserImage(user)" 
                                :alt="getDisplayName(user)"
                                class="avatar-image"
                                @error="handleImageError"
                            />
                            <div v-else class="avatar-placeholder">
                                {{ getInitials(getDisplayName(user)) }}
                            </div>
                            <div class="status-ring" :class="{ 'active': user.active }"></div>
                        </div>
                        <div class="card-body">
                            <h5 class="card-name">{{ getDisplayName(user) }}</h5>
                            <span class="card-role">{{ formatRole(user.userrole) }}</span>
                            <div class="card-details">
                                <div class="detail-item">
                                    <b-icon icon="person" size="xs"></b-icon>
                                    <span>{{ user.username }}</span>
                                </div>
                                <div class="detail-item" v-if="getUserLocation(user)">
                                    <b-icon icon="geo-alt" size="xs"></b-icon>
                                    <span>{{ getUserLocation(user) }}</span>
                                </div>
                            </div>
                            <div class="card-status">
                                <span class="status-badge" :class="{ 'active': user.active }">
                                    {{ user.active ? 'Active' : 'Inactive' }}
                                </span>
                            </div>
                        </div>
                        <div class="card-actions">
                            <b-button 
                                size="sm" 
                                variant="outline-secondary"
                                @click.stop="viewUser(user)"
                                title="View Details"
                            >
                                <b-icon icon="eye"></b-icon>
                            </b-button>
                            <b-button 
                                v-if="isAdmin"
                                size="sm" 
                                variant="outline-secondary"
                                @click.stop="editUser(user)"
                                title="Edit User"
                            >
                                <b-icon icon="pencil"></b-icon>
                            </b-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- User Details Modal -->
        <b-modal 
            id="modal-hierarchy-user" 
            scrollable 
            :title="selectedUser ? getDisplayName(selectedUser) : 'User Details'"
            size="lg"
            hide-footer
            centered
        >
            <div v-if="selectedUser" class="user-detail-modal">
                <div class="modal-header-custom">
                    <div class="modal-avatar">
                        <img 
                            v-if="getUserImage(selectedUser)" 
                            :src="getUserImage(selectedUser)" 
                            :alt="getDisplayName(selectedUser)"
                            class="modal-avatar-image"
                            @error="handleImageError"
                        />
                        <div v-else class="avatar-placeholder large">
                            {{ getInitials(getDisplayName(selectedUser)) }}
                        </div>
                    </div>
                    <div class="modal-title">
                        <h3>{{ getDisplayName(selectedUser) }}</h3>
                        <div class="modal-tags">
                            <span class="role-tag">{{ formatRole(selectedUser.userrole) }}</span>
                            <span class="status-tag" :class="{ 'active': selectedUser.active }">
                                {{ selectedUser.active ? 'Active' : 'Inactive' }}
                            </span>
                        </div>
                    </div>
                </div>
                
                <div class="modal-body-custom">
                    <div class="info-grid">
                        <div class="info-item">
                            <label>Username</label>
                            <p>{{ selectedUser.username }}</p>
                        </div>
                        <div class="info-item">
                            <label>Email</label>
                            <p>{{ selectedUser.email || 'N/A' }}</p>
                        </div>
                        <div class="info-item">
                            <label>Location</label>
                            <p>{{ getUserLocation(selectedUser) || 'N/A' }}</p>
                        </div>
                        <div class="info-item">
                            <label>Joined</label>
                            <p>{{ formatDate(selectedUser.ts) }}</p>
                        </div>
                        <div class="info-item" v-if="selectedUser.commission_percentage">
                            <label>Commission</label>
                            <p>{{ selectedUser.commission_percentage }}%</p>
                        </div>
                        <div class="info-item" v-if="selectedUser.multiple_locations_enabled === 'true'">
                            <label>Multiple Locations</label>
                            <p><span class="badge badge-secondary">Enabled</span></p>
                        </div>
                    </div>
                    
                    <div v-if="selectedUser.properties && Object.keys(selectedUser.properties).length > 0" class="properties-section">
                        <label>Additional Properties</label>
                        <pre class="properties-json">{{ JSON.stringify(selectedUser.properties, null, 2) }}</pre>
                    </div>
                </div>
                
                <div class="modal-footer-custom">
                    <b-button variant="secondary" @click="$bvModal.hide('modal-hierarchy-user')">
                        Close
                    </b-button>
                    <b-button 
                        v-if="isAdmin"
                        variant="primary" 
                        @click="editUser(selectedUser)"
                    >
                        <b-icon icon="pencil" class="mr-1"></b-icon>
                        Edit User
                    </b-button>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
    import Requestor from '@/utilities/Requestor'
    import Loading from 'vue-loading-overlay'
    import 'vue-loading-overlay/dist/vue-loading.css'

    export default {
        name: "UserHierarchy",
        components: { Loading },
        data: function() {
            return {
                packet: {},
                users: [],
                filteredUsers: [],
                items_being_loaded: false,
                viewMode: 'org',
                searchQuery: '',
                roleFilter: null,
                statusFilter: null,
                showOnlyActive: true,
                selectedUser: null,
                highlightUserId: null,
                
                roleFilterOptions: [
                    { value: 'admin', text: 'Admin' },
                    { value: 'accountmanager', text: 'Account Manager' },
                    { value: 'officeuser', text: 'Office User' },
                    { value: 'officemanager', text: 'Office Manager' },
                    { value: 'locationadmin', text: 'Location Admin' },
                    { value: 'locationuser', text: 'Location User' },
                    { value: 'locationstockperson', text: 'Location Stock Person' },
                    { value: 'salesperson', text: 'Sales Person' },
                    { value: 'warehouseuser', text: 'Warehouse User' },
                    { value: 'warehousemanager', text: 'Warehouse Manager' },
                    { value: 'accountsuser', text: 'Accounts User' },
                    { value: 'housetruck', text: 'House Truck' },
                    { value: 'deliverytruck', text: 'Delivery Truck' }
                ],
                statusFilterOptions: [
                    { value: true, text: 'Active' },
                    { value: false, text: 'Inactive' }
                ]
            }
        },
        computed: {
            isAdmin() {
                return this.packet.userDetails && this.packet.userDetails.userrole === 'admin';
            },
            
            hasActiveFilters() {
                return this.searchQuery || this.roleFilter !== null || this.statusFilter !== null;
            }
        },
        methods: {
            getDisplayName: function(user) {
                if (!user) return 'Unknown';
                if (user.fullname && user.fullname.trim() !== '' && user.fullname !== 'null' && user.fullname !== 'undefined') {
                    return user.fullname.trim();
                }
                if (user.username && user.username.trim() !== '' && user.username !== 'null' && user.username !== 'undefined') {
                    return user.username.trim();
                }
                return 'User ' + (user.id || '');
            },
            
            fetchUsers: function() {
                var that = this;
                var path = "/api/user/list_users/";
                var data = {};
                
                var success = function(response) {
                    if (response.data.error) {
                        that.$bvToast.toast(
                            response.data.error,
                            { title: "Error", autoHideDelay: 1500, variant: "warning" }
                        );
                    } else {
                        that.users = response.data.result || [];
                        that.filteredUsers = that.users;
                        if (that.showOnlyActive) {
                            that.filterHierarchy();
                        }
                    }
                    that.items_being_loaded = false;
                };
                
                var failure = function(error) {
                    console.error('Error loading users:', error);
                    that.$bvToast.toast(
                        "Error loading users. Please refresh.",
                        { title: "Error", autoHideDelay: 1500, variant: "warning" }
                    );
                    that.items_being_loaded = false;
                };
                
                this.items_being_loaded = true;
                Requestor.getSyncP(path, data, this, success, failure);
            },
            
            getAllUsers: function() {
                return this.filteredUsers;
            },
            
            getUsersByRole: function(roles) {
                var roleArray = Array.isArray(roles) ? roles : [roles];
                return this.filteredUsers.filter(function(user) {
                    return roleArray.includes(user.userrole);
                });
            },
            
            getWarehouseGroups: function() {
                var that = this;
                var locationMap = {};
                
                var managers = this.getUsersByRole('warehousemanager');
                managers.forEach(function(manager) {
                    var location = that.getUserLocation(manager) || 'Unknown';
                    if (!locationMap[location]) {
                        locationMap[location] = {
                            location: location,
                            manager: manager,
                            users: []
                        };
                    } else {
                        if (!locationMap[location].manager) {
                            locationMap[location].manager = manager;
                        }
                    }
                });
                
                var warehouseUsers = this.getUsersByRole('warehouseuser');
                warehouseUsers.forEach(function(user) {
                    var location = that.getUserLocation(user) || 'Unknown';
                    if (locationMap[location]) {
                        locationMap[location].users.push(user);
                    } else {
                        if (!locationMap[location]) {
                            locationMap[location] = {
                                location: location,
                                manager: null,
                                users: []
                            };
                        }
                        locationMap[location].users.push(user);
                    }
                });
                
                var result = Object.keys(locationMap).map(function(location) {
                    var group = locationMap[location];
                    group.totalUsers = (group.manager ? 1 : 0) + group.users.length;
                    return group;
                });
                
                result.sort(function(a, b) {
                    return a.location.localeCompare(b.location);
                });
                
                return result.filter(function(group) {
                    return group.manager || group.users.length > 0;
                });
            },
            
            getLocationGroups: function() {
                var that = this;
                var locationMap = {};
                
                var admins = this.getUsersByRole('locationadmin');
                admins.forEach(function(admin) {
                    var location = that.getUserLocation(admin) || 'Unknown';
                    if (!locationMap[location]) {
                        locationMap[location] = {
                            location: location,
                            admin: admin,
                            salespersons: []
                        };
                    } else {
                        if (!locationMap[location].admin) {
                            locationMap[location].admin = admin;
                        }
                    }
                });
                
                var salespersons = this.getUsersByRole('salesperson');
                salespersons.forEach(function(sp) {
                    var location = that.getUserLocation(sp) || 'Unknown';
                    if (locationMap[location]) {
                        locationMap[location].salespersons.push(sp);
                    } else {
                        if (!locationMap[location]) {
                            locationMap[location] = {
                                location: location,
                                admin: null,
                                salespersons: []
                            };
                        }
                        locationMap[location].salespersons.push(sp);
                    }
                });
                
                var result = Object.keys(locationMap).map(function(location) {
                    var group = locationMap[location];
                    group.totalUsers = (group.admin ? 1 : 0) + group.salespersons.length;
                    return group;
                });
                
                result.sort(function(a, b) {
                    return a.location.localeCompare(b.location);
                });
                
                return result.filter(function(group) {
                    return group.admin || group.salespersons.length > 0;
                });
            },
            
            filterHierarchy: function() {
                var that = this;
                var searchLower = this.searchQuery ? this.searchQuery.toLowerCase() : '';
                
                this.filteredUsers = this.users.filter(function(user) {
                    var matchesSearch = true;
                    if (searchLower) {
                        var displayName = that.getDisplayName(user).toLowerCase();
                        var username = (user.username || '').toLowerCase();
                        matchesSearch = displayName.includes(searchLower) || username.includes(searchLower);
                    }
                    
                    var matchesRole = true;
                    if (that.roleFilter !== null) {
                        matchesRole = user.userrole === that.roleFilter;
                    }
                    
                    var matchesStatus = true;
                    if (that.statusFilter !== null) {
                        matchesStatus = user.active === that.statusFilter;
                    }
                    
                    var matchesActive = true;
                    if (that.showOnlyActive) {
                        matchesActive = user.active === true;
                    }
                    
                    return matchesSearch && matchesRole && matchesStatus && matchesActive;
                });
            },
            
            refreshHierarchy: function() {
                this.searchQuery = '';
                this.roleFilter = null;
                this.statusFilter = null;
                this.showOnlyActive = true;
                this.fetchUsers();
            },
            
            selectUser: function(user) {
                this.selectedUser = user;
                this.highlightUserId = user.id;
            },
            
            viewUser: function(user) {
                this.selectedUser = user;
                this.$bvModal.show('modal-hierarchy-user');
            },
            
            editUser: function(user) {
                this.$emit('edit-user', user);
                this.$bvModal.hide('modal-hierarchy-user');
            },
            
            getUserImage: function(user) {
                if (!user) return null;
                
                if (user.image_url) {
                    var imageUrl = user.image_url;
                    if (typeof imageUrl === 'string') {
                        imageUrl = imageUrl.trim();
                    }
                    if (imageUrl && imageUrl !== '' && imageUrl !== 'null' && imageUrl !== 'undefined') {
                        return imageUrl;
                    }
                }
                
                if (user.properties && user.properties.image_url) {
                    var imageUrl = user.properties.image_url;
                    if (typeof imageUrl === 'string') {
                        imageUrl = imageUrl.trim();
                    }
                    if (imageUrl && imageUrl !== '' && imageUrl !== 'null' && imageUrl !== 'undefined') {
                        return imageUrl;
                    }
                }
                
                return null;
            },
            
            handleImageError: function(event) {
                event.target.style.display = 'none';
            },
            
            getInitials: function(name) {
                if (!name || name === 'Unknown') return '?';
                var parts = name.split(' ');
                if (parts.length >= 2) {
                    return (parts[0][0] + parts[1][0]).toUpperCase();
                }
                if (name.length >= 2) {
                    return name.substring(0, 2).toUpperCase();
                }
                return name.substring(0, 1).toUpperCase();
            },
            
            getUserLocation: function(user) {
                var that = this;
                if (!user) return null;
                
                if (user.location_id) {
                    return this.getLocationName(user.location_id);
                }
                
                if (user.multiple_locations_enabled === 'true' && user.location_ids) {
                    var locationIds = user.location_ids;
                    if (typeof locationIds === 'string') {
                        try {
                            locationIds = JSON.parse(locationIds);
                        } catch(e) {
                            locationIds = [];
                        }
                    }
                    if (Array.isArray(locationIds) && locationIds.length > 0) {
                        var locationNames = locationIds
                            .map(function(id) { 
                                return that.getLocationName(id); 
                            })
                            .filter(function(name) { 
                                return name !== 'N/A'; 
                            });
                        return locationNames.join(', ');
                    }
                }
                
                if (user.properties) {
                    if (user.properties.location_id) {
                        return this.getLocationName(user.properties.location_id);
                    }
                    if (user.properties.location_ids && Array.isArray(user.properties.location_ids)) {
                        var locationNames = user.properties.location_ids
                            .map(function(id) { 
                                return that.getLocationName(id); 
                            })
                            .filter(function(name) { 
                                return name !== 'N/A'; 
                            });
                        return locationNames.join(', ');
                    }
                }
                
                return null;
            },
            
            getLocationName: function(locationId) {
                if (!locationId) return 'N/A';
                if (this.packet.locationsdict) {
                    return this.packet.locationsdict[locationId] || 'N/A';
                }
                return 'Location ' + locationId;
            },
            
            formatRole: function(role) {
                if (!role) return 'N/A';
                var roleMap = {
                    'admin': 'Admin',
                    'accountmanager': 'Account Manager',
                    'officeuser': 'Office User',
                    'officemanager': 'Office Manager',
                    'locationadmin': 'Location Admin',
                    'locationuser': 'Location User',
                    'locationstockperson': 'Location Stock Person',
                    'salesperson': 'Sales Person',
                    'warehouseuser': 'Warehouse User',
                    'warehousemanager': 'Warehouse Manager',
                    'accountsuser': 'Accounts User',
                    'housetruck': 'House Truck',
                    'deliverytruck': 'Delivery Truck'
                };
                return roleMap[role] || role;
            },
            
            formatDate: function(dateString) {
                if (!dateString) return 'N/A';
                try {
                    var date = new Date(dateString);
                    return date.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                    });
                } catch (error) {
                    return dateString;
                }
            }
        },
        created: function() {
            this.packet = JSON.parse(localStorage.getItem('packet')) || {};
            this.packet.userDetails = JSON.parse(localStorage.getItem('userDetails')) || {};
            this.fetchUsers();
        }
    }
</script>

<style scoped>
/* ===== Container ===== */
.hierarchy-container {
    padding: 16px;
    background: #f7fafc;
    min-height: 85vh;
}

/* ===== Header ===== */
.hierarchy-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 18px;
    background: white;
    border-radius: 6px;
    box-shadow: 0 1px 2px rgba(0,0,0,0.04);
    margin-bottom: 12px;
}

.header-left .hierarchy-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #1a202c;
    margin: 0;
}

.hierarchy-subtitle {
    color: #718096;
    font-size: 0.7rem;
    margin: 1px 0 0;
}

.header-right .btn-group .btn {
    border-radius: 4px;
    padding: 3px 8px;
    font-size: 0.7rem;
}

.header-right .btn-group .btn-primary {
    background: #2d3748;
    border-color: #2d3748;
}

/* ===== Controls ===== */
.hierarchy-controls {
    display: flex;
    gap: 8px;
    margin-bottom: 12px;
    flex-wrap: wrap;
    background: white;
    padding: 6px 12px;
    border-radius: 6px;
    box-shadow: 0 1px 2px rgba(0,0,0,0.04);
    align-items: center;
}

.search-wrapper {
    flex: 1;
    position: relative;
    min-width: 140px;
}

.search-icon {
    position: absolute;
    left: 8px;
    top: 50%;
    transform: translateY(-50%);
    color: #a0aec0;
    font-size: 0.7rem;
}

.search-input {
    padding-left: 26px;
    padding-right: 26px;
    border-radius: 4px;
    border: 1px solid #e2e8f0;
    height: 30px;
    font-size: 0.75rem;
    background: #f7fafc;
}

.search-input:focus {
    border-color: #4a5568;
    background: white;
    box-shadow: 0 0 0 3px rgba(74, 85, 104, 0.06);
}

.clear-icon {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    color: #a0aec0;
    cursor: pointer;
    font-size: 0.7rem;
}

.filter-wrapper {
    display: flex;
    gap: 4px;
    align-items: center;
}

.filter-select {
    width: 90px;
    border-radius: 4px;
    border: 1px solid #e2e8f0;
    height: 30px;
    background: #f7fafc;
    font-size: 0.7rem;
}

.status-toggle {
    display: flex;
    align-items: center;
    margin-left: 2px;
    font-size: 0.7rem;
}

/* ===== Loading ===== */
.loading-container {
    text-align: center;
    padding: 30px 20px;
}

.loader {
    width: 28px;
    height: 28px;
    border: 3px solid #e2e8f0;
    border-top: 3px solid #4a5568;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 8px;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

.loading-container p {
    color: #718096;
    font-size: 0.75rem;
}

/* ============================================ */
/* ORG CHART - MAIN LAYOUT */
/* ============================================ */
.org-chart-wrapper {
    padding: 2px 0;
}

.org-tree {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
}

/* ============================================ */
/* ADMIN ROW */
/* ============================================ */
.admin-row {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 6px;
    padding: 2px 0;
}

.admin-item {
    display: flex;
    flex-direction: column;
    align-items: center;
}

/* ============================================ */
/* BRANCHES ROW - SIDE BY SIDE */
/* ============================================ */
.branches-row {
    display: flex;
    flex-direction: row;
    width: 100%;
    gap: 12px;
    justify-content: center;
    padding: 0 4px;
}

.branch-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 0;
    max-width: 50%;
}

.branch-warehouse {
    border-right: 1.5px solid #e2e8f0;
    padding-right: 10px;
}

.branch-locations {
    padding-left: 10px;
}

/* ============================================ */
/* TREE LEVELS */
/* ============================================ */
.tree-level {
    width: 100%;
    padding: 4px 0;
}

.level-label-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    margin-bottom: 4px;
}

.level-icon { font-size: 0.7rem; }
.level-name { font-size: 0.6rem; font-weight: 600; color: #4a5568; }

.level-count {
    background: #edf2f7;
    color: #4a5568;
    font-size: 0.45rem;
    font-weight: 600;
    padding: 1px 6px;
    border-radius: 8px;
}

.branch-label {
    justify-content: flex-start;
    padding-left: 4px;
}

/* ============================================ */
/* LOCATION & WAREHOUSE GROUPS */
/* ============================================ */
.location-groups-container,
.warehouse-groups-container {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 6px;
    width: 100%;
    padding: 2px 0;
}

.location-group-card,
.warehouse-group-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    background: white;
    border-radius: 4px;
    border: 1px solid #e2e8f0;
    padding: 4px 8px;
    min-width: 80px;
    flex: 1 1 auto;
    max-width: 140px;
}

.location-group-header,
.warehouse-group-header {
    display: flex;
    align-items: center;
    gap: 2px;
    padding: 1px 6px;
    border-radius: 8px;
    margin-bottom: 3px;
    width: 100%;
    justify-content: center;
}

.location-group-header {
    background: #f0f4ff;
}

.warehouse-group-header {
    background: #f0f8f0;
}

.location-icon { font-size: 0.55rem; }
.location-name { font-size: 0.55rem; font-weight: 600; color: #2d3748; }
.location-count { font-size: 0.4rem; color: #4a5568; background: white; padding: 0 4px; border-radius: 4px; }

/* ============================================ */
/* NODE CARDS */
/* ============================================ */
.node-card {
    background: white;
    border-radius: 4px;
    padding: 4px 6px;
    border: 1.5px solid #e2e8f0;
    box-shadow: 0 1px 2px rgba(0,0,0,0.03);
    min-width: 60px;
    max-width: 80px;
    text-align: center;
    position: relative;
    transition: all 0.15s ease;
}

.node-card:hover {
    box-shadow: 0 2px 6px rgba(0,0,0,0.05);
}

.org-node {
    cursor: pointer;
    transition: all 0.15s ease;
}

.org-node:hover {
    transform: translateY(-2px);
    z-index: 10;
}

.org-node.highlighted .node-card {
    border-color: #4a5568;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

.org-node.inactive .node-card {
    opacity: 0.4;
}

.org-node.active-user .node-card {
    border-color: #48bb78;
}

/* Level Colors */
.level-1 { border-color: #2d3748 !important; }
.level-2 { border-color: #4a5568 !important; }
.level-3 { border-color: #a0aec0 !important; }
.level-4 { border-color: #cbd5e0 !important; }

/* ============================================ */
/* NODE AVATAR */
/* ============================================ */
.node-avatar {
    display: flex;
    justify-content: center;
    margin-bottom: 1px;
    position: relative;
}

.avatar-image {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    object-fit: cover;
    border: 1.5px solid #e2e8f0;
}

.avatar-placeholder {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 9px;
}

.admin-avatar { background: #2d3748; }
.manager-avatar { background: #4a5568; }
.staff-avatar { background: #718096; }
.truck-avatar { background: #a0aec0; }

.avatar-placeholder.large {
    width: 56px;
    height: 56px;
    font-size: 18px;
}

/* Status Ring */
.status-ring {
    position: absolute;
    bottom: -1px;
    right: -1px;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    border: 1.5px solid white;
    background: #a0aec0;
}

.status-ring.active {
    background: #48bb78;
}

/* ============================================ */
/* NODE INFO */
/* ============================================ */
.node-info {
    text-align: center;
}

.node-name {
    font-weight: 500;
    color: #1a202c;
    font-size: 0.5rem;
    margin-bottom: 0px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.node-role {
    font-size: 0.4rem;
    color: #718096;
    font-weight: 400;
}

/* ============================================ */
/* LEVEL NODES */
/* ============================================ */
.level-nodes {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 3px;
    padding: 0 2px;
}

.node-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
}

/* Section Labels */
.sales-label,
.staff-label {
    font-size: 0.35rem;
    font-weight: 600;
    color: #718096;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    background: #edf2f7;
    padding: 0 4px;
    border-radius: 3px;
    margin-bottom: 2px;
    margin-top: 1px;
}

/* ============================================ */
/* CONNECTORS */
/* ============================================ */
.tree-connector {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding: 2px 0;
    position: relative;
}

.tree-connector.split-connector {
    padding: 4px 0 6px 0;
}

.connector-line {
    background: #cbd5e0;
}

.connector-line.vertical {
    width: 1.5px;
    height: 8px;
}

.connector-line.vertical.small {
    height: 4px;
}

.connector-line.horizontal {
    height: 1.5px;
    width: 30%;
    position: relative;
}

.connector-line.horizontal.split-line {
    width: 40%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: none;
    position: relative;
}

.connector-line.horizontal.split-line::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    width: 18%;
    height: 1.5px;
    background: #cbd5e0;
    transform: translateY(-50%);
}

.connector-line.horizontal.split-line::after {
    content: '';
    position: absolute;
    right: 0;
    top: 50%;
    width: 18%;
    height: 1.5px;
    background: #cbd5e0;
    transform: translateY(-50%);
}

.connector-line.vertical.left {
    position: absolute;
    left: 12%;
    top: 0;
    height: 8px;
}

.connector-line.vertical.right {
    position: absolute;
    right: 12%;
    top: 0;
    height: 8px;
}

.split-left-label,
.split-right-label {
    font-size: 0.4rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    color: #4a5568;
    background: #edf2f7;
    padding: 1px 6px;
    border-radius: 3px;
    position: relative;
    z-index: 1;
}

.location-connector {
    display: flex;
    justify-content: center;
    padding: 1px 0;
}

/* ============================================ */
/* CARD VIEW */
/* ============================================ */
.card-view {
    padding: 4px 0;
}

.user-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 8px;
}

.user-card {
    background: white;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
    padding: 8px;
    transition: all 0.15s ease;
    cursor: pointer;
    position: relative;
    box-shadow: 0 1px 2px rgba(0,0,0,0.03);
}

.user-card:hover {
    border-color: #a0aec0;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
    transform: translateY(-2px);
}

.user-card.selected {
    border-color: #4a5568;
    background: #f7fafc;
}

.user-card.inactive { opacity: 0.4; }
.user-card.active-user { border-color: #48bb78; }

.card-avatar {
    text-align: center;
    margin-bottom: 3px;
    position: relative;
}

.card-avatar .avatar-image {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid #e2e8f0;
}

.card-avatar .avatar-placeholder {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #4a5568;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 10px;
    margin: 0 auto;
}

.card-body { text-align: center; }
.card-name { font-weight: 600; color: #1a202c; margin-bottom: 0px; font-size: 0.7rem; }
.card-role { font-size: 0.5rem; color: #718096; font-weight: 400; display: inline-block; margin-bottom: 2px; }

.card-details {
    text-align: left;
    margin: 2px 0;
    font-size: 0.55rem;
    color: #4a5568;
}

.detail-item {
    display: flex;
    align-items: center;
    gap: 2px;
    padding: 0px 0;
}

.card-status { margin-top: 2px; }

.status-badge {
    font-size: 0.45rem;
    font-weight: 600;
    padding: 0 8px;
    border-radius: 4px;
    background: #edf2f7;
    color: #a0aec0;
}

.status-badge.active {
    background: #e6fffa;
    color: #38a169;
}

.card-actions {
    position: absolute;
    top: 4px;
    right: 4px;
    display: flex;
    gap: 2px;
    opacity: 0;
    transition: opacity 0.15s ease;
}

.user-card:hover .card-actions { opacity: 1; }
.card-actions .btn { padding: 1px 4px; border-radius: 3px; font-size: 0.55rem; }

/* ============================================ */
/* EMPTY STATE */
/* ============================================ */
.empty-state {
    text-align: center;
    padding: 30px 20px;
    background: white;
    border-radius: 6px;
    border: 1.5px dashed #e2e8f0;
}

.empty-icon { color: #a0aec0; margin-bottom: 6px; }
.empty-state h4 { color: #1a202c; margin-bottom: 2px; font-size: 0.8rem; }
.empty-state p { color: #718096; margin-bottom: 6px; font-size: 0.7rem; }

/* ============================================ */
/* USER DETAIL MODAL */
/* ============================================ */
.user-detail-modal { padding: 0; }

.modal-header-custom {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    background: #f7fafc;
    border-bottom: 1px solid #e2e8f0;
}

.modal-avatar-image {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid #e2e8f0;
}

.modal-title h3 {
    margin: 0 0 1px;
    color: #1a202c;
    font-size: 0.85rem;
}

.modal-tags { display: flex; gap: 3px; align-items: center; }
.role-tag { font-size: 0.5rem; padding: 0 8px; border-radius: 4px; background: #edf2f7; color: #4a5568; }
.status-tag { font-size: 0.45rem; padding: 0 8px; border-radius: 4px; font-weight: 600; background: #edf2f7; color: #a0aec0; }
.status-tag.active { background: #e6fffa; color: #38a169; }

.modal-body-custom { padding: 8px 12px; }

.info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 4px;
    margin-bottom: 6px;
}

.info-item label { font-size: 0.4rem; font-weight: 600; color: #a0aec0; text-transform: uppercase; display: block; }
.info-item p { margin: 0; color: #1a202c; font-weight: 500; font-size: 0.65rem; }

.properties-section { margin-top: 4px; }
.properties-section label { font-size: 0.4rem; font-weight: 600; color: #a0aec0; text-transform: uppercase; }

.properties-json {
    background: #f7fafc;
    border: 1px solid #e2e8f0;
    border-radius: 3px;
    padding: 4px 8px;
    font-size: 0.55rem;
    overflow: auto;
    max-height: 60px;
    margin-top: 2px;
}

.modal-footer-custom {
    display: flex;
    justify-content: flex-end;
    gap: 4px;
    padding: 6px 12px;
    border-top: 1px solid #e2e8f0;
    background: #f7fafc;
    border-radius: 0 0 6px 6px;
}

/* ============================================ */
/* RESPONSIVE */
/* ============================================ */
@media (max-width: 992px) {
    .branches-row {
        flex-direction: column;
        gap: 8px;
    }
    
    .branch-container {
        max-width: 100%;
        width: 100%;
    }
    
    .branch-warehouse {
        border-right: none;
        border-bottom: 1.5px solid #e2e8f0;
        padding-right: 0;
        padding-bottom: 8px;
    }
    
    .branch-locations {
        padding-left: 0;
        padding-top: 4px;
    }
}

@media (max-width: 768px) {
    .hierarchy-container { padding: 8px; }
    
    .hierarchy-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
        padding: 6px 10px;
    }
    
    .header-right { width: 100%; }
    .header-right .btn-group { width: 100%; }
    .header-right .btn-group .btn { flex: 1; }
    
    .hierarchy-controls { flex-direction: column; padding: 6px 8px; }
    .filter-wrapper { width: 100%; flex-wrap: wrap; }
    .filter-select { flex: 1; width: auto; min-width: 50px; }
    .status-toggle { width: 100%; justify-content: center; margin-left: 0; margin-top: 2px; }
    
    .level-nodes { flex-direction: column; align-items: center; }
    
    .node-card {
        min-width: 60px;
        max-width: 80px;
        padding: 4px 6px;
    }
    
    .location-group-card,
    .warehouse-group-card {
        width: 100%;
        max-width: 100%;
        min-width: unset;
    }
    
    .location-groups-container,
    .warehouse-groups-container {
        flex-direction: column;
        align-items: center;
    }
    
    .branches-row { gap: 6px; }
    
    .connector-line.horizontal.split-line {
        flex-direction: column;
        gap: 2px;
        padding: 2px 0;
        background: none;
    }
    
    .connector-line.horizontal.split-line::before,
    .connector-line.horizontal.split-line::after {
        display: none;
    }
    
    .split-left-label, .split-right-label {
        font-size: 0.35rem;
        padding: 1px 4px;
    }
    
    .connector-line.vertical.left,
    .connector-line.vertical.right {
        display: none;
    }
    
    .user-grid { grid-template-columns: 1fr; }
    
    .modal-header-custom {
        flex-direction: column;
        text-align: center;
        padding: 8px 10px;
    }
    
    .modal-tags { justify-content: center; flex-wrap: wrap; }
    .modal-body-custom { padding: 6px 8px; }
    .info-grid { grid-template-columns: 1fr; }
    .modal-footer-custom { flex-direction: column; padding: 4px 8px; }
    .modal-footer-custom .btn { width: 100%; }
    .card-actions { opacity: 1; }
}

@media (max-width: 480px) {
    .hierarchy-container { padding: 4px; }
    .node-card { min-width: 50px; max-width: 70px; padding: 3px 5px; }
    .node-name { font-size: 0.4rem; }
    .avatar-image { width: 18px; height: 18px; }
    .avatar-placeholder { width: 18px; height: 18px; font-size: 7px; }
}
</style>


// WEBPACK FOOTER //
// src/components/Users/UserHierarchy.vue